import React, { useState, useRef, useEffect, useCallback } from "react";
import swarnaLogo from "../assets/swarna_logo_placeholder.js";
import InventoryPage from "./Inventory";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  GridViewIcon, Invoice02Icon, ReturnRequestIcon, DocumentValidationIcon,
  PackageIcon, ShoppingCart01Icon, Wrench01Icon, UserAdd01Icon,
  ListViewIcon, Analytics01Icon,
  Share01Icon, Cancel01Icon, MoreVerticalIcon,
  FloppyDiskIcon, CreditCardIcon, PrinterIcon,
  Copy01Icon, Delete02Icon,
  AddCircleIcon, StarIcon, Search01Icon, ArrowDown01Icon, ArrowRight01Icon,
  PencilEdit02Icon, Diamond01Icon, DiamondIcon,
  NecklaceIcon, EarRingsIcon, GemIcon, GymnasticRingsIcon, Watch01Icon,
} from "@hugeicons/core-free-icons";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  gold: "#5B7CFA",
  goldLight: "#EEF2FF",
  goldMid: "#4363E8",
  goldDark: "#314CCB",
  bg: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceHigh: "#F8FAFC",
  surfaceBorder: "#E2E8F0",
  surfaceBorderHigh: "#CBD5E1",
  text: "#0F172A",
  textMuted: "#475569",
  textDim: "#94A3B8",
  blue: "#2563EB",
  blueLight: "#EFF6FF",
  green: "#16A34A",
  greenLight: "#F0FDF4",
  red: "#DC2626",
  redLight: "#FEF2F2",
  amber: "#D97706",
  amberLight: "#FFFBEB",
  purple: "#6366F1",
  purpleLight: "#EEF2FF",
};

// ─── Google Font import trick via style injection ─────────────────────────────
const FONT_STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
body { background: ${T.bg}; font-family: 'DM Sans', sans-serif; color: ${T.text}; }
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: ${T.surface}; }
::-webkit-scrollbar-thumb { background: ${T.surfaceBorderHigh}; border-radius: 2px; }
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
`;

// ─── Live gold rates ──────────────────────────────────────────────────────────
const INITIAL_RATES = { "24K": 9240, "22K": 8470, "18K": 6930, silver: 107 };

const CATALOGUE = [
  { tag: "SS-1001", product: "Gold Ring",     purity: "91.6", gwt: "4.5",  making: 900  },
  { tag: "SS-1002", product: "Diamond Ring",  purity: "75",   gwt: "3.2",  making: 1800 },
  { tag: "SS-1003", product: "Gold Chain",    purity: "91.6", gwt: "8.0",  making: 600  },
  { tag: "SS-1004", product: "Gold Necklace", purity: "91.6", gwt: "15.0", making: 750  },
  { tag: "SS-1005", product: "Gold Earrings", purity: "91.6", gwt: "3.0",  making: 850  },
  { tag: "SS-1006", product: "Gold Bangle",   purity: "91.6", gwt: "10.0", making: 700  },
  { tag: "SS-1007", product: "Gold Bracelet", purity: "75",   gwt: "6.0",  making: 1100 },
  { tag: "SS-1008", product: "Gold Pendant",  purity: "91.6", gwt: "2.5",  making: 950  },
  { tag: "SS-1009", product: "Silver Anklet", purity: "—",    gwt: "12.0", making: 300  },
  { tag: "SS-1010", product: "Platinum Ring", purity: "—",    gwt: "3.5",  making: 2200 },
];

// ─── Bills list mock data ─────────────────────────────────────────────────────
const BILLS_DATA = [
  { id: "SL-2026-4892", date: "24 Mar 2026", customer: "Meena Patel",    phone: "+91 98745 63210", items: 3, total: 127840, status: "Draft" },
  { id: "SL-2026-4891", date: "24 Mar 2026", customer: "Arjun Nair",     phone: "+91 93301 44556", items: 2, total:  54200, status: "Saved" },
  { id: "SL-2026-4890", date: "23 Mar 2026", customer: "Rekha Mehta",    phone: "+91 98765 43210", items: 5, total: 289200, status: "Paid"  },
  { id: "SL-2026-4889", date: "23 Mar 2026", customer: "Vijay Patel",    phone: "+91 91123 45678", items: 2, total:  45600, status: "Paid"  },
  { id: "SL-2026-4888", date: "23 Mar 2026", customer: "Hema Iyer",      phone: "+91 82234 55667", items: 1, total:  22400, status: "Paid"  },
  { id: "SL-2026-4887", date: "21 Mar 2026", customer: "Anita Shah",     phone: "+91 99887 76655", items: 7, total: 514300, status: "Saved" },
  { id: "SL-2026-4886", date: "21 Mar 2026", customer: "Ramesh Gupta",   phone: "+91 97654 32109", items: 1, total:  18750, status: "Paid"  },
  { id: "SL-2026-4885", date: "20 Mar 2026", customer: "Sunita Joshi",   phone: "+91 94455 66778", items: 4, total: 198400, status: "Paid"  },
  { id: "SL-2026-4884", date: "20 Mar 2026", customer: "Kavita Rane",    phone: "+91 88990 01122", items: 6, total: 342600, status: "Paid"  },
  { id: "SL-2026-4883", date: "19 Mar 2026", customer: "Pradeep Desai",  phone: "+91 70011 22334", items: 2, total:  67500, status: "Paid"  },
  { id: "SL-2026-4882", date: "19 Mar 2026", customer: "Nalini Sharma",  phone: "+91 96678 89900", items: 3, total: 142800, status: "Paid"  },
  { id: "SL-2026-4881", date: "19 Mar 2026", customer: "Deepak Joshi",   phone: "+91 85567 78899", items: 1, total:  31500, status: "Paid"  },
];

// ─── Starter items ────────────────────────────────────────────────────────────
const STARTER_ITEMS = [
  { id: 1, sub: false, tag: "TG-2026-001", product: "Gold Bangle Set (Kada)", size: "2.6",  pcs: 1, purity: "91.6", gwt: 48.2, swt: 0,   making: 750,  stoneAmt: 0     },
  { id: 2, sub: false, tag: "TG-2026-006", product: "Gold Earrings Jhumka",   size: "",     pcs: 1, purity: "91.6", gwt: 9.8,  swt: 0,   making: 950,  stoneAmt: 0     },
  { id: 3, sub: false, tag: "TG-2026-015", product: "Pearl Earrings (Gold)",  size: "",     pcs: 1, purity: "91.6", gwt: 6.2,  swt: 1.8, making: 1400, stoneAmt: 12000 },
];

let _nextId = 10;
const nextId = () => ++_nextId;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
const fmtN = (n, d = 2) => parseFloat(n || 0).toFixed(d);

function calcItem(item, rates) {
  const purity = parseFloat(item.purity);
  if (!purity || isNaN(purity)) return { nwt: 0, metalVal: 0, total: 0 };
  const rateKey = purity >= 99 ? "24K" : purity >= 91 ? "22K" : "18K";
  const rate = rates[rateKey] || rates["22K"];
  const nwt = Math.max(0, parseFloat(item.gwt || 0) - parseFloat(item.swt || 0));
  const metalVal = nwt * rate;
  const making = parseFloat(item.making || 0) * nwt;
  const stone = parseFloat(item.stoneAmt || 0);
  return { nwt, metalVal, making, stone, total: metalVal + making + stone };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function GoldTicker({ rates, setRates }) {
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setRates(r => ({
        ...r,
        "24K": r["24K"] + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 3),
        "22K": r["22K"] + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 3),
      }));
    }, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ padding: "10px 16px", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.green, animation: "pulse 2s infinite", flexShrink: 0 }} />
          <span style={{ fontSize: 9, fontWeight: 600, color: T.textDim, letterSpacing: 0.8, textTransform: "uppercase" }}>Rates</span>
        </div>
        <button onClick={() => setEdit(e => !e)} style={{ fontSize: 9, color: T.textDim, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
          {edit ? "done" : "edit"}
        </button>
      </div>
      {[["24K", "24K"], ["22K", "22K"], ["18K", "18K"], ["Silver", "silver"]].map(([label, key]) => (
        <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: T.textDim }}>{label}</span>
          {edit
            ? <input type="number" value={rates[key]} onChange={e => setRates(r => ({ ...r, [key]: +e.target.value }))}
                style={{ width: 64, background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 4, padding: "1px 5px", fontSize: 10, color: T.text, textAlign: "right", fontFamily: "DM Sans, sans-serif", outline: "none" }} />
            : <span style={{ fontSize: 11, fontWeight: 500, color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>₹{rates[key].toLocaleString("en-IN")}</span>
          }
        </div>
      ))}
    </div>
  );
}

function NavItem({ icon, label, active, badge, onClick }) {
  return (
    <div onClick={onClick} className="nav-item-wrap" style={{
      display: "flex", alignItems: "center", gap: 9, padding: "8px 16px",
      cursor: "pointer", fontSize: 13, fontWeight: active ? 500 : 400,
      color: active ? T.gold : T.textMuted,
      background: active ? "rgba(91,124,250,0.08)" : "transparent",
      borderLeft: `2px solid ${active ? T.gold : "transparent"}`,
      transition: "all 0.15s",
    }}>
      <span style={{ fontSize: 14, width: 16, textAlign: "center", flexShrink: 0 }}>{icon}</span>
      <span className="sidebar-text" style={{ flex: 1 }}>{label}</span>
      {badge && <span className="sidebar-text" style={{ fontSize: 9, background: T.gold, color: T.bg, padding: "0 4px", borderRadius: 3, fontWeight: 600, letterSpacing: 0.4, lineHeight: "14px", display: "inline-block" }}>{badge}</span>}
    </div>
  );
}

function Field({ label, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: 0.5, textTransform: "uppercase", textAlign: "left" }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`,
  borderRadius: 7, padding: "7px 10px", fontSize: 12, color: T.text,
  fontFamily: "DM Sans, sans-serif", outline: "none", width: "100%",
  transition: "border-color 0.15s",
};


function Select({ value, onChange, options, style: s }) {
  const [focused, setFocused] = useState(false);
  return (
    <select value={value} onChange={onChange}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ ...inputStyle, borderColor: focused ? T.gold : T.surfaceBorder, cursor: "pointer", ...s }}>
      {options.map(o => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.v} value={o.v}>{o.l}</option>
      )}
    </select>
  );
}

function Btn({ children, variant = "ghost", onClick, style: s, small }) {
  const [hov, setHov] = useState(false);
  const variants = {
    ghost: { bg: hov ? T.surfaceHigh : "transparent", border: T.surfaceBorderHigh, color: T.text },
    primary: { bg: hov ? T.goldDark : T.gold, border: T.gold, color: T.bg },
    success: { bg: hov ? "#2A6B45" : T.green, border: T.green, color: "#fff" },
    danger: { bg: hov ? "#3D1818" : T.redLight, border: T.red, color: T.red },
    outline: { bg: hov ? "rgba(91,124,250,0.1)" : "transparent", border: T.goldDark, color: T.gold },
  };
  const v = variants[variant];
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: small ? "4px 10px" : "7px 14px", background: v.bg, border: `0.5px solid ${v.border}`, borderRadius: 8, color: v.color, fontSize: small ? 11 : 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s", whiteSpace: "nowrap", ...s }}>
      {children}
    </button>
  );
}

function PurityBadge({ value }) {
  if (!value || value === "—") return <span style={{ color: T.textDim, fontSize: 11 }}>—</span>;
  const p = parseFloat(value);
  const color = p >= 99 ? T.green : p >= 91 ? T.gold : T.blue;
  const bg = p >= 99 ? T.greenLight : p >= 91 ? T.amberLight : T.blueLight;
  return <span style={{ background: bg, color, border: `0.5px solid ${color}30`, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: "2px 7px", fontVariantNumeric: "tabular-nums" }}>{value}</span>;
}

function TCell({ children, align = "left", muted, bold, style: s }) {
  return (
    <td style={{ padding: "5px 8px", fontSize: 12, color: muted ? T.textMuted : bold ? T.gold : T.text, fontWeight: bold ? 600 : 400, textAlign: align, verticalAlign: "middle", borderBottom: `0.5px solid ${T.surfaceBorder}`, ...s }}>
      {children}
    </td>
  );
}

function TableInput({ value, onChange, type = "text", width = 60, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ width, background: focused ? T.surface : "transparent", border: focused ? `0.5px solid ${T.gold}` : "0.5px solid transparent", borderRadius: 5, padding: "3px 5px", fontSize: 11, color: T.text, fontFamily: "DM Sans, sans-serif", outline: "none", transition: "all 0.15s" }}
    />
  );
}

// ─── Icon helper ──────────────────────────────────────────────────────────────
const HI = ({ icon, size = 14 }) => (
  <HugeiconsIcon icon={icon} size={size} color="currentColor" strokeWidth={1.5} />
);

function getProductStyle(name = "") {
  const n = name.toLowerCase();
  if (n.includes("ring"))                            return { bg: "#EDE9FE", color: "#7C3AED", icon: GemIcon };
  if (n.includes("necklace") || n.includes("haar"))  return { bg: T.amberLight, color: T.amber, icon: NecklaceIcon };
  if (n.includes("chain"))                           return { bg: "#FEF9C3", color: "#A16207", icon: NecklaceIcon };
  if (n.includes("bangle") || n.includes("kada"))    return { bg: T.blueLight, color: T.blue, icon: GymnasticRingsIcon };
  if (n.includes("earring") || n.includes("ear"))    return { bg: T.greenLight, color: T.green, icon: EarRingsIcon };
  if (n.includes("pendant") || n.includes("locket")) return { bg: T.goldLight, color: T.goldDark, icon: DiamondIcon };
  if (n.includes("bracelet"))                        return { bg: "#FCE7F3", color: "#BE185D", icon: Watch01Icon };
  return { bg: T.surfaceHigh, color: T.textDim, icon: Diamond01Icon };
}

// Two curated jewellery images — preloaded at module parse time so they're cached before first hover
const JEWELLERY_IMG_STONE = "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=460&h=320&q=80";
const JEWELLERY_IMG_CHAIN  = "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=460&h=320&q=80";
[JEWELLERY_IMG_STONE, JEWELLERY_IMG_CHAIN].forEach(src => { const img = new Image(); img.src = src; });
function getProductImage(name = "") {
  const n = name.toLowerCase();
  if (n.includes("chain") || n.includes("necklace") || n.includes("bangle") || n.includes("bracelet") || n.includes("anklet"))
    return JEWELLERY_IMG_CHAIN;
  return JEWELLERY_IMG_STONE;
}

// ─── Bills list sub-component ─────────────────────────────────────────────────

function BillsList({ onNew, onOpen }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const statusColor = { Draft: T.amber, Saved: T.blue, Paid: T.green };
  const statusBg    = { Draft: T.amberLight, Saved: T.blueLight, Paid: T.greenLight };

  const counts = ["All", "Draft", "Saved", "Paid"].reduce((acc, s) => {
    acc[s] = s === "All" ? BILLS_DATA.length : BILLS_DATA.filter(b => b.status === s).length;
    return acc;
  }, {});

  const filtered = BILLS_DATA.filter(b => {
    const q = search.toLowerCase();
    const matchSearch = !search || b.customer.toLowerCase().includes(q) || b.id.toLowerCase().includes(q);
    const matchStatus = statusFilter === "All" || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const groups = filtered.reduce((acc, b) => {
    if (!acc[b.date]) acc[b.date] = [];
    acc[b.date].push(b);
    return acc;
  }, {});

  const AVATAR_COLORS = ["#5B7CFA","#16A34A","#D97706","#6366F1","#DC2626","#2563EB","#0891B2","#7C3AED"];
  const avatarColor = name => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  const initials = name => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontFamily: "DM Serif Display, serif", color: T.text }}>Bills</div>
        <Btn variant="primary" onClick={onNew}><HI icon={AddCircleIcon} /> New Bill</Btn>
      </div>

      {/* Search */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", marginBottom: 12 }}>
        <span style={{ position: "absolute", left: 10, color: T.textMuted, pointerEvents: "none", display: "flex", alignItems: "center" }}>
          <HI icon={Search01Icon} size={13} />
        </span>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by customer or bill number..."
          style={{ ...inputStyle, width: "100%", padding: "8px 28px" }}
          onFocus={e => e.target.style.borderColor = T.gold}
          onBlur={e => e.target.style.borderColor = T.surfaceBorder} />
      </div>

      {/* Status filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {["All", "Draft", "Saved", "Paid"].map(s => {
          const on = statusFilter === s;
          const color = s === "All" ? T.textMuted : statusColor[s];
          const bg    = s === "All" ? T.surfaceHigh : statusBg[s];
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: on ? 600 : 400, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s", background: on ? bg : "transparent", border: `0.5px solid ${on ? color : T.surfaceBorder}`, color: on ? color : T.textDim }}>
              {s}
              <span style={{ fontSize: 9, opacity: 0.75 }}>{counts[s]}</span>
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {Object.keys(groups).length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: T.textDim, fontSize: 13 }}>No bills found</div>
      ) : Object.entries(groups).map(([date, bills]) => (
        <div key={date} style={{ marginBottom: 20 }}>

          {/* Date label + rule */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>{date}</span>
            <div style={{ flex: 1, height: "0.5px", background: T.surfaceBorder }} />
          </div>

          {/* Bill rows */}
          {bills.map(b => (
            <div key={b.id} onClick={() => onOpen(b)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 8px", borderRadius: 8, cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHigh}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

              {/* Avatar */}
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${avatarColor(b.customer)}14`, border: `0.5px solid ${avatarColor(b.customer)}40`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 11, fontWeight: 700, color: avatarColor(b.customer), fontFamily: "DM Sans, sans-serif" }}>
                {initials(b.customer)}
              </div>

              {/* Customer + bill info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{b.customer}</span>
                  <span style={{ background: statusBg[b.status], color: statusColor[b.status], border: `0.5px solid ${statusColor[b.status]}40`, borderRadius: 20, fontSize: 9, fontWeight: 600, padding: "2px 7px", lineHeight: "14px" }}>{b.status}</span>
                </div>
                <div style={{ fontSize: 10, color: T.textDim, marginTop: 3, lineHeight: 1 }}>
                  {b.id} · {b.items} items
                </div>
              </div>

              {/* Amount + open */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: "DM Serif Display, serif" }}>₹{b.total.toLocaleString("en-IN")}</span>
                <Btn small onClick={e => { e.stopPropagation(); onOpen(b); }}>Open →</Btn>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function JewelleryBilling() {
  const [rates, setRates] = useState(INITIAL_RATES);
  const [activeNav, setActiveNav] = useState("billing");
  const [invLocFilter, setInvLocFilter] = useState("All");
  const [items, setItems] = useState(STARTER_ITEMS);
  const [customer, setCustomer] = useState({ name: "Meena Patel", phone: "+91 98745 63210", since: 2021, purchases: 7, balance: 0, advance: 25000, scheme: { name: "Kanchan Gold Plan", months: 8, total: 12, credit: 24000 } });
  const [billDate] = useState(new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }));
  const [salesperson, setSalesperson] = useState("Priya Sharma");
  const [occasion, setOccasion] = useState("General");
  const [rateType, setRateType] = useState("Today's Rate");
  const [gstType, setGstType] = useState("GST Exclusive");
  const [makingBasis, setMakingBasis] = useState("Per gram");
  const [discount, setDiscount] = useState(0);
  const [payMode, setPayMode] = useState("Card");
  const [tendered, setTendered] = useState("");
  const [remarks, setRemarks] = useState("");
  const [exchangeWt, setExchangeWt] = useState("");
  const [exchangeMetal, setExchangeMetal] = useState("Gold 22K");
  const [schemeApplied, setSchemeApplied] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const searchHoverTimerRef = useRef(null);
  const [showRemarks, setShowRemarks] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showNewBillModal, setShowNewBillModal] = useState(false);
  const [newBillInput, setNewBillInput] = useState("");
  const [showBreakup, setShowBreakup] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("list");
  const [scannerConnected] = useState(true); // toggle false to simulate disconnected
  const [openTabs, setOpenTabs] = useState([]);

  const startNewBill = useCallback(() => {
    setNewBillInput("");
    setShowNewBillModal(true);
  }, []);

  const confirmNewBill = useCallback((input) => {
    const isPhone = /\d{5,}/.test(input.trim());
    setCustomer(prev => ({
      ...prev,
      name: isPhone ? "New Customer" : input.trim() || "New Customer",
      phone: isPhone ? input.trim() : "",
    }));
    setItems([]);
    setOpenTabs(prev => [...prev.filter(t => t.id !== "new"), { id: "new", customer: isPhone ? input.trim() : input.trim() || "New Customer" }]);
    setActiveTab("new");
    setShowNewBillModal(false);
  }, []);

  const openBill = useCallback((bill) => {
    setOpenTabs(prev => prev.find(t => t.id === bill.id) ? prev : [...prev, bill]);
    setActiveTab(bill.id);
  }, []);

  const closeTab = useCallback((id, e) => {
    e.stopPropagation();
    setOpenTabs(prev => prev.filter(t => t.id !== id));
    setActiveTab(prev => prev === id ? "list" : prev);
  }, []);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(null);
  const [searchHovered, setSearchHovered] = useState(null);
  const [itemsView, setItemsView] = useState("cards");

  useEffect(() => {
    if (payMode !== "Cash") setTendered("");
  }, [payMode]);


  // ── Derived totals ──
  const itemCalcs = items.map(it => calcItem(it, rates));
  const metalVal = itemCalcs.reduce((s, c) => s + c.metalVal, 0);
  const makingTotal = itemCalcs.reduce((s, c) => s + (c.making || 0), 0);
  const stoneTotal = itemCalcs.reduce((s, c) => s + (c.stone || 0), 0);
  const subtotal = metalVal + makingTotal + stoneTotal;
  const gstRate = gstType === "No GST" ? 0 : 0.03;
  const gstAmt = subtotal * gstRate;
  const exchangeVal = exchangeWt ? parseFloat(exchangeWt) * rates["22K"] : 0;
  const schemeCredit = schemeApplied ? customer.scheme.credit : 0;
  const discountAmt = (subtotal * parseFloat(discount || 0)) / 100;
  const prevBal = customer.balance < 0 ? Math.abs(customer.balance) : 0; // old unpaid dues
  const advancePaid = customer.advance || 0;                              // advance already paid for this order
  const grandTotal = subtotal + gstAmt - exchangeVal - schemeCredit - discountAmt + prevBal - advancePaid;
  const totalPcs = items.reduce((s, i) => s + parseInt(i.pcs || 0), 0);
  const totalGwt = items.reduce((s, i) => s + parseFloat(i.gwt || 0), 0);
  const totalSwt = items.reduce((s, i) => s + parseFloat(i.swt || 0), 0);
  const totalNwt = itemCalcs.reduce((s, c) => s + c.nwt, 0);

  const updateItem = useCallback((id, field, val) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, [field]: val } : it));
  }, []);

  const addRow = (tag = "") => setItems(prev => [...prev, { id: nextId(), sub: false, tag, product: "", size: "", pcs: 1, purity: "91.6", gwt: "", swt: 0, making: 900, stoneAmt: 0 }]);
  const deleteRow = (id) => setItems(prev => prev.filter(it => it.id !== id));

  const handleSave = () => {};

  const navItems = [
    { key: "dashboard", icon: <HI icon={GridViewIcon} />,         label: "Dashboard" },
    { key: "billing",   icon: <HI icon={Invoice02Icon} />,        label: "Billing", badge: "NEW" },
    { key: "returns",   icon: <HI icon={ReturnRequestIcon} />,    label: "Sales Returns" },
    { key: "estimates", icon: <HI icon={DocumentValidationIcon} />, label: "Estimates" },
    { key: "inventory", icon: <HI icon={PackageIcon} />,          label: "Inventory" },
    { key: "orders",    icon: <HI icon={ShoppingCart01Icon} />,   label: "Orders" },
    { key: "repairs",   icon: <HI icon={Wrench01Icon} />,         label: "Repairs" },
    { key: "karigar",   icon: <HI icon={UserAdd01Icon} />,        label: "Karigar" },
    { key: "ledger",    icon: <HI icon={ListViewIcon} />,         label: "Ledger" },
    { key: "reports",   icon: <HI icon={Analytics01Icon} />,      label: "Reports" },
  ];

  return (
    <>
      <style>{FONT_STYLE}</style>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(16px)} to{opacity:1;transform:translateX(0)} }
        .row-hover:hover td { background: rgba(91,124,250,0.06) !important; }
        .nav-hover:hover { background: rgba(91,124,250,0.08) !important; color: ${T.text} !important; }

        /* ── Responsive ─────────────────────────────────────────── */
        .hamburger-btn { display: none; }

        @media (max-width: 1024px) {
          .layout-grid { grid-template-columns: 52px 1fr !important; }
          .sidebar-text { display: none !important; }
          .sidebar-aux { display: none !important; }
          .nav-item-wrap { padding: 10px 0 !important; justify-content: center !important; gap: 0 !important; }
          .topbar-secondary { display: none !important; }
          .topbar-bill-meta { display: none !important; }
          .bill-header-row { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          .customer-identity { flex: none !important; }
          .bill-fields-grid { grid-template-columns: 1fr 1fr !important; }
          .exchange-grid { grid-template-columns: 1fr 1fr !important; }
          .footer-main-bar { flex-wrap: wrap !important; gap: 10px !important; }
          .footer-divider { display: none !important; }
          .footer-payment-zone { min-width: 100% !important; order: 1; }
          .footer-total-zone { min-width: 100% !important; order: 2; justify-content: space-between !important; border-top: 0.5px solid ${T.surfaceBorder} !important; padding-top: 8px !important; }
        }

        @media (max-width: 640px) {
          .layout-grid { grid-template-columns: 1fr !important; }
          .erp-sidebar {
            position: fixed !important; top: 0 !important; left: 0 !important;
            height: 100vh !important; z-index: 200 !important; width: 216px !important;
            transform: translateX(-216px) !important;
            transition: transform 0.25s ease !important;
            box-shadow: 4px 0 20px rgba(0,0,0,0.12) !important;
          }
          .erp-sidebar.sidebar-open { transform: translateX(0) !important; }
          .erp-sidebar.sidebar-open .sidebar-text { display: block !important; }
          .erp-sidebar.sidebar-open .sidebar-aux { display: block !important; }
          .erp-sidebar.sidebar-open .nav-item-wrap { padding: 8px 16px !important; justify-content: flex-start !important; gap: 9px !important; }
          .hamburger-btn { display: flex !important; }
        }
      `}</style>

      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 199 }} />}

      <div className="layout-grid" style={{ display: "grid", gridTemplateColumns: "216px 1fr", height: "100vh", overflow: "hidden", background: T.bg }}>

        {/* ── Sidebar ── */}
        <div className={`erp-sidebar${sidebarOpen ? " sidebar-open" : ""}`} style={{ background: T.surface, borderRight: `0.5px solid ${T.surfaceBorder}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Logo */}
          <div style={{ height: 38, padding: "0 14px", borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", alignItems: "center", gap: 9, flexShrink: 0 }}>
            <img src={swarnaLogo} alt="Swarna Square" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
            <span className="sidebar-text" style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "DM Serif Display, serif", letterSpacing: 0.3, whiteSpace: "nowrap" }}>Swarna Square</span>
          </div>

          {/* Gold ticker */}
          <div className="sidebar-aux"><GoldTicker rates={rates} setRates={setRates} /></div>

          {/* Nav */}
          <div style={{ flex: 1, overflowY: "auto", paddingBottom: 12 }}>
            <div className="sidebar-aux" style={{ padding: "10px 0 4px", fontSize: 9, fontWeight: 600, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", paddingLeft: 16, marginTop: 4 }}>Operations</div>
            {navItems.slice(0, 4).map(n => <NavItem key={n.key} {...n} active={activeNav === n.key} onClick={() => { setActiveNav(n.key); if (n.key === "billing") setActiveTab("list"); setSidebarOpen(false); }} />)}
            <div className="sidebar-aux" style={{ padding: "10px 0 4px", fontSize: 9, fontWeight: 600, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", paddingLeft: 16, marginTop: 6 }}>Management</div>
            <NavItem key="inventory" {...navItems[4]} active={activeNav === "inventory"} onClick={() => { setActiveNav("inventory"); setSidebarOpen(false); }} />
            {activeNav === "inventory" && (
              <div className="sidebar-text">
                {[
                  { loc: "All",      color: T.textDim  },
                  { loc: "Floor",    color: T.green    },
                  { loc: "Vault",    color: T.blue     },
                  { loc: "Karigar",  color: T.amber    },
                  { loc: "Approval", color: T.purple   },
                ].map(({ loc, color }) => (
                  <div key={loc} onClick={() => setInvLocFilter(loc)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 16px 5px 38px", cursor: "pointer", fontSize: 12, color: invLocFilter === loc ? T.text : T.textDim, background: invLocFilter === loc ? `${T.gold}08` : "transparent", borderLeft: `2px solid ${invLocFilter === loc ? T.gold : "transparent"}`, transition: "all 0.15s", fontWeight: invLocFilter === loc ? 500 : 400 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    {loc}
                  </div>
                ))}
              </div>
            )}
            {navItems.slice(5, 9).map(n => <NavItem key={n.key} {...n} active={activeNav === n.key} onClick={() => { setActiveNav(n.key); setSidebarOpen(false); }} />)}
            <div className="sidebar-aux" style={{ padding: "10px 0 4px", fontSize: 9, fontWeight: 600, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", paddingLeft: 16, marginTop: 6 }}>Finance</div>
            {navItems.slice(9).map(n => <NavItem key={n.key} {...n} active={activeNav === n.key} onClick={() => { setActiveNav(n.key); setSidebarOpen(false); }} />)}
          </div>

          {/* User */}
          <div style={{ padding: "12px 14px", borderTop: `0.5px solid ${T.surfaceBorder}`, display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${T.goldDark}, ${T.gold})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: T.bg, flexShrink: 0 }}>PS</div>
            <div className="sidebar-text">
              <div style={{ fontSize: 12, fontWeight: 500, color: T.text }}>Priya Sharma</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Counter 1 · Billing</div>
            </div>
          </div>
        </div>

        {/* ── Main ── */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg }}>

          {activeNav === "inventory" ? <InventoryPage rates={rates} locFilter={invLocFilter} setLocFilter={setInvLocFilter} /> : <>

          {/* ── Tab bar — always visible ── */}
          <div style={{ background: T.surfaceHigh, borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", alignItems: "stretch", height: 38, flexShrink: 0, overflowX: "auto" }}>
            {/* Bills (list) tab */}
            <button onClick={() => setActiveTab("list")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 16px", fontSize: 12, fontWeight: activeTab === "list" ? 600 : 400, color: activeTab === "list" ? T.text : T.textMuted, background: activeTab === "list" ? T.bg : "transparent", border: "none", borderRight: `0.5px solid ${T.surfaceBorder}`, borderBottom: activeTab === "list" ? `2px solid ${T.gold}` : "2px solid transparent", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}>
              <HI icon={Invoice02Icon} size={12} /> Bills
            </button>
            {/* Open bill tabs */}
            {openTabs.map(tab => (
              <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 12px 0 14px", fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? T.text : T.textMuted, background: activeTab === tab.id ? T.bg : "transparent", borderRight: `0.5px solid ${T.surfaceBorder}`, borderBottom: activeTab === tab.id ? `2px solid ${T.gold}` : "2px solid transparent", cursor: "pointer", flexShrink: 0, userSelect: "none" }}>
                <HI icon={Invoice02Icon} size={12} />
                <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tab.id}</span>
                <span style={{ fontSize: 10, color: T.textDim, marginLeft: 1 }}>· {tab.customer}</span>
                <button onClick={e => closeTab(tab.id, e)} style={{ marginLeft: 4, background: "none", border: "none", cursor: "pointer", color: T.textDim, display: "flex", alignItems: "center", padding: 2, borderRadius: 3, lineHeight: 1 }}>✕</button>
              </div>
            ))}
            {/* Scanner indicator */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "0 14px", borderLeft: `0.5px solid ${T.surfaceBorder}`, flexShrink: 0 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: scannerConnected ? T.green : T.surfaceBorderHigh, flexShrink: 0, animation: scannerConnected ? "pulse 2s infinite" : "none" }} />
              <span style={{ fontSize: 10, color: scannerConnected ? T.textMuted : T.textDim }}>
                {scannerConnected ? "Scanner" : "No scanner"}
              </span>
            </div>
            {/* New bill button */}
            <div style={{ display: "flex", alignItems: "center", padding: "0 14px", borderLeft: `0.5px solid ${T.surfaceBorder}` }}>
              <Btn small onClick={startNewBill}><HI icon={AddCircleIcon} size={12} /> New Bill</Btn>
            </div>
          </div>

          {activeTab === "list" ? (
            <BillsList onNew={startNewBill} onOpen={openBill} />
          ) : (
            <>
              {/* ── Bill Header ── */}
              <div className="bill-header-row" style={{ background: T.surface, borderBottom: `0.5px solid ${T.surfaceBorder}`, padding: "0 20px", display: "flex", alignItems: "stretch", flexShrink: 0, minHeight: 72 }}>

                {/* Zone 1 — Avatar + stacked identity */}
                <div className="customer-identity" style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0, paddingRight: 24, paddingTop: 16, paddingBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#EEF2FF", border: "1px solid #C7D2FE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#4338CA", flexShrink: 0, letterSpacing: 0.5 }}>MP</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Row 1: Name + visits badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, lineHeight: 1 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{customer.name}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, color: T.textDim, background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 10, padding: "2px 6px" }}>{customer.purchases} visits</span>
                    </div>
                    {/* Row 2: Phone */}
                    <span style={{ fontSize: 10, color: T.textMuted, lineHeight: 1 }}>{customer.phone}</span>
                    {/* Row 3: Bill # · date */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4, lineHeight: 1 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace" }}>{activeTab === "new" ? "SL-2026-4892" : activeTab}</span>
                      <span style={{ color: T.surfaceBorderHigh, fontSize: 9 }}>·</span>
                      <span style={{ fontSize: 10, color: T.textDim }}>{billDate}</span>
                    </div>
                  </div>
                </div>

                {/* Zone 2 — Bill config */}
                <div className="bill-fields-grid" style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 24px", flex: 1 }}>
                  {[
                    { label: "Staff", value: salesperson, onChange: e => setSalesperson(e.target.value), options: ["Priya Sharma", "Ravi Kumar", "Meera Iyer"] },
                    { label: "Occasion", value: occasion, onChange: e => setOccasion(e.target.value), options: ["General", "Wedding", "Anniversary", "Birthday", "Festival"] },
                  ].map(({ label, value, onChange, options }) => (
                    <div key={label} style={{ position: "relative", flexShrink: 0, display: "inline-flex", alignItems: "center" }}>
                      <select value={value} onChange={onChange} style={{ appearance: "none", background: "transparent", border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 20, outline: "none", fontSize: 11, color: T.text, padding: "6px 28px 6px 12px", cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
                        {options.map(o => (
                          <option key={o} value={o}>{label} — {o}</option>
                        ))}
                      </select>
                      <span style={{ position: "absolute", right: 10, pointerEvents: "none", color: T.textDim, display: "flex", alignItems: "center" }}>
                        <HI icon={ArrowDown01Icon} size={11} />
                      </span>
                    </div>
                  ))}
                </div>

                {/* Zone 3 — Actions */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", paddingLeft: 24 }}>
                  <span style={{ background: T.amber, color: "#fff", fontSize: 9, fontWeight: 600, padding: "1px 7px", borderRadius: 4, letterSpacing: 0.4, lineHeight: "18px" }}>Draft</span>
                  <Btn small onClick={handleSave} style={{ padding: "6px 8px" }}><HI icon={FloppyDiskIcon} size={13} /></Btn>
                  <Btn small style={{ padding: "6px 8px" }}><HI icon={Share01Icon} size={13} /></Btn>
                  <Btn small style={{ padding: "6px 8px" }}><HI icon={PrinterIcon} size={13} /></Btn>
                  <div style={{ position: "relative" }}>
                    <Btn small onClick={() => setShowMoreMenu(v => !v)} style={{ padding: "6px 8px" }}><HI icon={MoreVerticalIcon} size={13} /></Btn>
                    {showMoreMenu && (
                      <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(15,23,42,0.1)", zIndex: 50, minWidth: 140, overflow: "hidden" }} onClick={() => setShowMoreMenu(false)}>
                        <button style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "none", border: "none", fontSize: 12, color: T.red, cursor: "pointer", fontFamily: "DM Sans, sans-serif", textAlign: "left" }}
                          onMouseEnter={e => e.currentTarget.style.background = T.redLight}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}>
                          <HI icon={Cancel01Icon} size={13} /> Cancel Bill
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

                {/* ── Section 2: Item Details ── */}
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>

                  {/* Items section — flat, no box */}
                  <div style={{ background: T.surface }}>
                    <div style={{ padding: "10px 20px", display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: 0.5, textTransform: "uppercase", flex: 1 }}>Product Details</span>
                      <div style={{ display: "flex", gap: 2, background: T.surfaceHigh, borderRadius: 6, padding: 2, border: `0.5px solid ${T.surfaceBorder}` }}>
                        {[["table", ListViewIcon], ["cards", GridViewIcon]].map(([v, icon]) => (
                          <button key={v} onClick={() => setItemsView(v)} style={{ width: 26, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, border: "none", cursor: "pointer", transition: "all 0.15s", background: itemsView === v ? T.surface : "transparent", color: itemsView === v ? T.gold : T.textDim, boxShadow: itemsView === v ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                            <HI icon={icon} size={13} />
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Search bar — below header, dropdown via position:fixed */}
                    <div style={{ padding: "8px 20px", position: "relative" }}>
                      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        <span style={{ position: "absolute", left: 10, color: T.textMuted, pointerEvents: "none", display: "flex", alignItems: "center" }}><HI icon={Search01Icon} size={13} /></span>
                        <input
                          ref={searchInputRef}
                          value={productSearch}
                          onChange={e => { setProductSearch(e.target.value); setSearchOpen(true); }}
                          onFocus={() => setSearchOpen(true)}
                          onBlur={() => setTimeout(() => { setSearchOpen(false); setSearchExpanded(null); setSearchHovered(null); }, 150)}
                          onKeyDown={e => { if (e.key === "Enter" && productSearch.trim()) { addRow(productSearch.trim()); setProductSearch(""); setSearchOpen(false); setSearchExpanded(null); } }}
                          placeholder="Enter tag no. or search product name..."
                          style={{ ...inputStyle, width: "100%", fontSize: 12, padding: "8px 24px 8px 28px" }}
                        />
                        {productSearch && (
                          <button onClick={() => { setProductSearch(""); setSearchOpen(false); setSearchExpanded(null); }}
                            style={{ position: "absolute", right: 6, background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>✕</button>
                        )}
                      </div>
                      {/* Dropdown — two independent fixed panels */}
                      {searchOpen && (() => {
                        const rect = searchInputRef.current?.getBoundingClientRect();
                        if (!rect) return null;
                        const q = productSearch.toLowerCase();
                        const matches = q ? CATALOGUE.filter(p => p.tag.toLowerCase().includes(q) || p.product.toLowerCase().includes(q)) : CATALOGUE;
                        const highlight = (text) => {
                          if (!q) return text;
                          const idx = text.toLowerCase().indexOf(q);
                          if (idx === -1) return text;
                          return <>{text.slice(0, idx)}<span style={{ background: T.gold + "28", color: T.gold, fontWeight: 700, borderRadius: 2, padding: "0 1px" }}>{text.slice(idx, idx + q.length)}</span>{text.slice(idx + q.length)}</>;
                        };
                        const activeTag = searchExpanded || searchHovered;
                        const expProduct = activeTag ? matches.find(p => p.tag === activeTag) : null;
                        const topPos = rect.bottom + 4;
                        const listWidth = 320;
                        const cardLeft = rect.left + listWidth + 8;
                        const startHide = () => { searchHoverTimerRef.current = setTimeout(() => { setSearchHovered(null); setSearchExpanded(null); }, 180); };
                        const cancelHide = () => clearTimeout(searchHoverTimerRef.current);
                        return (
                          <>
                            {/* Suggestion list */}
                            <div
                              onMouseDown={e => e.preventDefault()}
                              onMouseEnter={cancelHide}
                              onMouseLeave={startHide}
                              style={{ position: "fixed", top: topPos, left: rect.left, width: listWidth, background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 8, boxShadow: "0 4px 20px rgba(15,23,42,0.12)", zIndex: 9999, overflow: "hidden", paddingTop: 8, paddingBottom: 8 }}>
                              {q && (
                                <div style={{ padding: "5px 10px 4px", borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span style={{ fontSize: 9, color: T.textMuted, fontFamily: "DM Mono, monospace", letterSpacing: 0.5 }}>RESULTS</span>
                                  <span style={{ fontSize: 9, color: T.textMuted }}>{matches.length} found</span>
                                </div>
                              )}
                              {matches.map((p, i) => {
                                const { bg, color, icon } = getProductStyle(p.product);
                                const pColor = p.purity === "99.9" ? T.green : p.purity === "91.6" ? T.amber : p.purity === "75" ? T.blue : T.textDim;
                                const pLabel = p.purity === "99.9" ? "24K" : p.purity === "91.6" ? "22K" : p.purity === "75" ? "18K" : "—";
                                const estCalc = calcItem({ purity: p.purity, gwt: p.gwt, making: p.making }, rates);
                                const isPinned = searchExpanded === p.tag;
                                const isActive = activeTag === p.tag;
                                return (
                                  <div key={i}
                                    onMouseEnter={() => { cancelHide(); setSearchHovered(p.tag); }}
                                    onMouseDown={() => {
                                      addRow();
                                      setItems(prev => {
                                        const last = prev[prev.length - 1];
                                        return prev.map(it => it.id === last.id ? { ...it, tag: p.tag, product: p.product, purity: p.purity, gwt: p.gwt, making: p.making } : it);
                                      });
                                      setProductSearch(""); setSearchOpen(false); setSearchExpanded(null); setSearchHovered(null);
                                    }}
                                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", borderBottom: `0.5px solid ${T.surfaceBorder}`, background: isActive ? T.surfaceHigh : "transparent" }}>
                                    <div style={{ width: 26, height: 26, borderRadius: 6, background: bg, border: `0.5px solid ${color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color }}>
                                      <HI icon={icon} size={13} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 5, lineHeight: 1 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace", letterSpacing: 0.3 }}>{highlight(p.tag)}</span>
                                        <span style={{ fontSize: 11, fontWeight: 500, color: T.text }}>{highlight(p.product)}</span>
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 5, lineHeight: 1 }}>
                                        {estCalc.total > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#000", fontFamily: "DM Serif Display, serif" }}>{fmt(estCalc.total)}</span>}
                                        <span style={{ fontSize: 9, color: T.textMuted }}>{p.gwt}g</span>
                                        {p.making > 0 && <span style={{ fontSize: 9, color: T.textMuted }}>₹{p.making}/g</span>}
                                      </div>
                                    </div>
                                    <span style={{ fontSize: 9, fontWeight: 600, color: pColor, background: pColor + "18", border: `0.5px solid ${pColor}30`, borderRadius: 3, padding: "2px 5px", flexShrink: 0 }}>{pLabel}</span>
                                    <span
                                      onMouseDown={e => { e.stopPropagation(); setSearchExpanded(v => v === p.tag ? null : p.tag); }}
                                      style={{ color: isPinned ? T.gold : T.textDim, flexShrink: 0, display: "flex", alignItems: "center", cursor: "pointer", padding: "2px" }}>
                                      <HI icon={ArrowRight01Icon} size={11} />
                                    </span>
                                  </div>
                                );
                              })}
                              <div
                                onMouseDown={() => { addRow(); setProductSearch(""); setSearchOpen(false); setSearchExpanded(null); setSearchHovered(null); }}
                                style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", cursor: "pointer", color: T.gold, fontSize: 10, background: "transparent" }}
                                onMouseEnter={e => e.currentTarget.style.background = T.surfaceHigh}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                <HI icon={AddCircleIcon} size={11} /> Add blank row
                              </div>
                            </div>
                            {/* Detail card — separate fixed panel */}
                            {expProduct && (() => {
                              const { bg, color, icon } = getProductStyle(expProduct.product);
                              const pColor = expProduct.purity === "99.9" ? T.green : expProduct.purity === "91.6" ? T.amber : expProduct.purity === "75" ? T.blue : T.textDim;
                              const pLabel = expProduct.purity === "99.9" ? "24K" : expProduct.purity === "91.6" ? "22K" : expProduct.purity === "75" ? "18K" : "—";
                              const estCalc = calcItem({ purity: expProduct.purity, gwt: expProduct.gwt, making: expProduct.making }, rates);
                              const rateKey = parseFloat(expProduct.purity) >= 99 ? "24K" : parseFloat(expProduct.purity) >= 91 ? "22K" : "18K";
                              const imgSrc = getProductImage(expProduct.product);
                              return (
                                <div
                                  onMouseDown={e => e.preventDefault()}
                                  onMouseEnter={cancelHide}
                                  onMouseLeave={startHide}
                                  style={{ position: "fixed", top: topPos, left: cardLeft, width: 228, background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 8, boxShadow: "0 4px 20px rgba(15,23,42,0.12)", zIndex: 9999, overflow: "hidden" }}>
                                  {/* Product image */}
                                  <div style={{ padding: "10px 10px 0" }}>
                                    <div style={{ height: 148, background: `linear-gradient(145deg, ${bg} 0%, ${color}25 100%)`, position: "relative", overflow: "hidden", borderRadius: 8 }}>
                                      {/* Fallback decorative layer */}
                                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <div style={{ position: "absolute", width: 140, height: 140, borderRadius: "50%", background: color + "14", top: -40, right: -30 }} />
                                        <div style={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", background: color + "18", bottom: -20, left: -10 }} />
                                        <div style={{ color, opacity: 0.25, position: "relative" }}><HI icon={icon} size={64} /></div>
                                      </div>
                                      {/* Real image — covers fallback when loaded */}
                                      <img
                                        src={imgSrc}
                                        alt={expProduct.product}
                                        onError={e => { e.target.style.display = "none"; }}
                                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                      {/* Overlays */}
                                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 48, background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%)" }} />
                                      <span style={{ position: "absolute", top: 8, right: 8, fontSize: 9, fontWeight: 700, color: pColor, background: "rgba(255,255,255,0.92)", border: `0.5px solid ${pColor}40`, borderRadius: 4, padding: "2px 7px", backdropFilter: "blur(4px)" }}>{pLabel}</span>
                                    </div>
                                  </div>
                                  {/* Name + tag */}
                                  <div style={{ padding: "10px 14px 9px", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.2, marginBottom: 4 }}>{expProduct.product}</div>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace", letterSpacing: 0.5 }}>{expProduct.tag}</div>
                                  </div>
                                  {/* Stats */}
                                  <div style={{ padding: "10px 14px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "9px 12px" }}>
                                    {[
                                      ["Gross Wt", `${expProduct.gwt} g`],
                                      ["Rate", fmt(rates[rateKey] || 0) + "/g"],
                                      ["Making", `₹${expProduct.making}/g`],
                                      ["Metal Value", fmt(estCalc.metalVal)],
                                      ["Making Amt", fmt(estCalc.making || 0)],
                                    ].map(([label, val]) => (
                                      <div key={label}>
                                        <div style={{ fontSize: 8, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1 }}>{label}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginTop: 3, lineHeight: 1 }}>{val}</div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Est. total + CTA */}
                                  <div style={{ padding: "8px 14px 14px" }}>
                                    <div style={{ background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 7, padding: "8px 10px", marginBottom: 10 }}>
                                      <div style={{ fontSize: 8, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Est. Total</div>
                                      <div style={{ fontSize: 17, fontWeight: 700, color: "#000", fontFamily: "DM Serif Display, serif", lineHeight: 1 }}>{fmt(estCalc.total)}</div>
                                    </div>
                                    <div onMouseDown={() => {
                                      addRow();
                                      setItems(prev => {
                                        const last = prev[prev.length - 1];
                                        return prev.map(it => it.id === last.id ? { ...it, tag: expProduct.tag, product: expProduct.product, purity: expProduct.purity, gwt: expProduct.gwt, making: expProduct.making } : it);
                                      });
                                      setProductSearch(""); setSearchOpen(false); setSearchExpanded(null); setSearchHovered(null);
                                    }} style={{ background: T.gold, color: "#fff", borderRadius: 7, padding: "7px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                      <HI icon={AddCircleIcon} size={12} /> Add to Bill
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        );
                      })()}
                    </div>
                    {itemsView === "cards" && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {items.length === 0 ? (
                          <div style={{ padding: "32px 20px", textAlign: "center" }}>
                            <div style={{ marginBottom: 8, color: T.textDim }}><HI icon={Diamond01Icon} size={36} /></div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 4 }}>No items yet</div>
                            <div style={{ fontSize: 12, color: T.textMuted }}>Search a product below or add a blank row manually</div>
                          </div>
                        ) : items.map((item, idx) => {
                          const calc = itemCalcs[idx];
                          const { bg, color, icon } = getProductStyle(item.product);
                          const purityColor = item.purity === "99.9" ? T.green : item.purity === "91.6" ? T.amber : item.purity === "75" ? T.blue : T.textDim;
                          const purityLabel = item.purity === "99.9" ? "24K" : item.purity === "91.6" ? "22K" : item.purity === "75" ? "18K" : "—";
                          const isExpanded = expandedRow === item.id;
                          const rateKey = parseFloat(item.purity) >= 99 ? "24K" : parseFloat(item.purity) >= 91 ? "22K" : "18K";
                          return (
                            <div key={item.id}>
                              {/* Main row */}
                              <div style={{ display: "flex", gap: 12, padding: "11px 20px", alignItems: "center", cursor: "pointer", position: "relative" }}
                                onClick={() => setExpandedRow(r => r === item.id ? null : item.id)}
                                onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHigh; setHoveredRow(item.id); }}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; setHoveredRow(null); }}>
                                {/* Icon */}
                                <div style={{ width: 34, height: 34, borderRadius: 7, background: bg, border: `0.5px solid ${color}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color }}>
                                  <HI icon={icon} size={17} />
                                </div>
                                {/* Details */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 5, lineHeight: 1 }}>
                                    {item.tag && <span style={{ fontSize: 9, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace", letterSpacing: 0.3 }}>{item.tag}</span>}
                                    {item.tag && <span style={{ color: T.surfaceBorderHigh, fontSize: 9 }}>·</span>}
                                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{item.product || <span style={{ color: T.textDim, fontStyle: "italic", fontWeight: 400 }}>Unnamed</span>}</span>
                                    <span style={{ fontSize: 9, fontWeight: 600, color: purityColor, background: purityColor + "18", border: `0.5px solid ${purityColor}30`, borderRadius: 3, padding: "1px 5px" }}>{purityLabel}</span>
                                  </div>
                                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, lineHeight: 1 }}>
                                    {[item.size && `Size ${item.size}`, item.gwt && `${item.gwt}g gross`, calc.nwt > 0 && `${fmtN(calc.nwt)}g net`, item.pcs > 1 && `${item.pcs} pcs`].filter(Boolean).join(" · ") || "—"}
                                  </div>
                                </div>
                                {/* Price */}
                                <div style={{ textAlign: "right", flexShrink: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "#000", fontFamily: "DM Serif Display, serif", lineHeight: 1 }}>{fmt(calc.total)}</div>
                                  {item.making > 0 && (() => {
                                    const catEntry = CATALOGUE.find(p => p.tag === item.tag);
                                    const isReduced = catEntry && parseFloat(item.making) < catEntry.making;
                                    return (
                                      <div style={{ fontSize: 9, color: T.textMuted, marginTop: 4, lineHeight: 1, display: "flex", alignItems: "center", gap: 4 }}>
                                        ₹{item.making}/g
                                        {isReduced && <span style={{ fontSize: 8, color: T.amber, background: T.amber + "20", border: `0.5px solid ${T.amber}40`, borderRadius: 2, padding: "0 3px", fontWeight: 600 }}>reduced</span>}
                                      </div>
                                    );
                                  })()}
                                </div>
                                {/* Actions — in flow, pushes price left on hover; reserved slot always present to avoid jump */}
                                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0, width: hoveredRow === item.id ? "auto" : 0, overflow: "hidden" }}>
                                  <button onClick={() => setItems(prev => { const pos = prev.findIndex(it => it.id === item.id); const copy = { ...item, id: nextId() }; const next = [...prev]; next.splice(pos + 1, 0, copy); return next; })}
                                    style={{ width: 26, height: 26, background: T.blueLight, border: `0.5px solid ${T.blue}40`, color: T.blue, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 5, flexShrink: 0 }}
                                    onMouseEnter={e => { e.currentTarget.style.background = T.blue; e.currentTarget.style.color = "#fff"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = T.blueLight; e.currentTarget.style.color = T.blue; }}>
                                    <HI icon={Copy01Icon} size={12} />
                                  </button>
                                  <button onClick={() => deleteRow(item.id)}
                                    style={{ width: 26, height: 26, background: T.redLight, border: `0.5px solid ${T.red}40`, color: T.red, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 5, flexShrink: 0 }}
                                    onMouseEnter={e => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}>
                                    <HI icon={Delete02Icon} size={12} />
                                  </button>
                                </div>
                                {/* Chevron */}
                                <span style={{ color: T.textDim, flexShrink: 0, display: "flex", alignItems: "center", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                                  <HI icon={ArrowDown01Icon} size={12} />
                                </span>
                              </div>
                              {/* Expanded detail panel */}
                              {isExpanded && (
                                <>
                                  <div style={{ borderTop: `0.5px solid ${T.surfaceBorder}`, margin: "0 20px" }} />
                                  <div style={{ padding: "10px 20px 12px 66px", background: T.surfaceHigh, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 16px" }}>
                                    {(() => {
                                      const catEntry = CATALOGUE.find(p => p.tag === item.tag);
                                      const origMaking = catEntry ? catEntry.making : null;
                                      const makingReduced = origMaking != null && parseFloat(item.making) < origMaking;
                                      const makingCell = (
                                        <div>
                                          <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 3 }}>
                                            <span style={{ fontSize: 10, color: T.textMuted }}>₹</span>
                                            <input type="number" value={item.making ?? ""} onChange={e => updateItem(item.id, "making", e.target.value)}
                                              style={{ width: 54, background: "transparent", border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 4, padding: "2px 5px", fontSize: 11, fontWeight: 600, color: T.text, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                                              onFocus={e => { e.target.style.borderColor = T.gold; e.target.style.background = T.surface; }}
                                              onBlur={e => { e.target.style.borderColor = T.surfaceBorder; e.target.style.background = "transparent"; }}
                                            />
                                            <span style={{ fontSize: 10, color: T.textMuted }}>/g</span>
                                          </div>
                                          {makingReduced && (
                                            <div style={{ fontSize: 8, color: T.amber, marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}>
                                              <span style={{ textDecoration: "line-through", color: T.textMuted }}>₹{origMaking}</span>
                                              <span style={{ background: T.amber + "20", border: `0.5px solid ${T.amber}40`, borderRadius: 2, padding: "0 3px", fontWeight: 600 }}>reduced</span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                      return [
                                        ["Purity", purityLabel + (item.purity && item.purity !== "—" ? ` (${item.purity}%)` : "")],
                                        ["Gross Wt", item.gwt ? `${item.gwt} g` : "—"],
                                        ["Stone Wt", item.swt ? `${item.swt} g` : "—"],
                                        ["Net Wt", calc.nwt > 0 ? `${fmtN(calc.nwt)} g` : "—"],
                                        ["Gold Rate", fmt(rates[rateKey] || 0) + "/g"],
                                        ["Making", null, makingCell],
                                        ["Metal Value", fmt(calc.metalVal)],
                                        ["Making Amt", fmt(calc.making || 0)],
                                        ...(calc.stone > 0 ? [["Stone Amt", fmt(calc.stone)]] : []),
                                        ...(item.size ? [["Size", item.size]] : []),
                                        ...(item.pcs > 1 ? [["Pieces", item.pcs]] : []),
                                      ].map(([label, value, custom]) => (
                                        <div key={label}>
                                          <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1 }}>{label}</div>
                                          {custom ?? <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginTop: 3, lineHeight: 1 }}>{value}</div>}
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </>
                              )}
                              {/* Inset row separator */}
                              <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 20px" }} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {itemsView === "table" && <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                        <thead>
                          <tr style={{ background: T.surfaceHigh }}>
                            {["#", "🏷 Tag", "Product", "↔ Size", "Qty", "◆ Purity", "Gr.Wt", "St.Wt", "Net Wt", "₹/g", "Making", "Stone", "Total"].map((h, i) => (
                              <th key={i} style={{ padding: "7px 8px", fontSize: 9, fontWeight: 600, color: T.textMuted, textAlign: "left", borderBottom: `0.5px solid ${T.surfaceBorder}`, letterSpacing: 0.5, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                            <th style={{ position: "sticky", right: 0, background: T.surfaceHigh, borderBottom: `0.5px solid ${T.surfaceBorder}`, width: 1 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={13} style={{ padding: "40px 20px", textAlign: "center" }}>
                                <div style={{ marginBottom: 8, color: T.textDim }}><HI icon={Diamond01Icon} size={36} /></div>
                                <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 4 }}>No items yet</div>
                                <div style={{ fontSize: 12, color: T.textMuted }}>Search a product below or add a blank row manually</div>
                              </td>
                            </tr>
                          ) : items.map((item, idx) => {
                            const calc = itemCalcs[idx];
                            const purityColor = item.purity === "99.9" ? T.green : item.purity === "91.6" ? T.amber : item.purity === "75" ? T.blue : T.textDim;
                            return (
                              <tr key={item.id} className="row-hover" style={{ animation: "fadeIn 0.2s ease" }}
                                onMouseEnter={() => setHoveredRow(item.id)}
                                onMouseLeave={() => setHoveredRow(null)}>
                                <TCell muted style={{ fontSize: 10, paddingLeft: item.sub ? 20 : 8 }}>{item.sub ? "↳" : idx + 1}</TCell>
                                <TCell><TableInput value={item.tag} onChange={v => updateItem(item.id, "tag", v)} width={80} /></TCell>
                                <TCell>
                                  {(() => { const { bg, color, icon } = getProductStyle(item.product); return (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <div style={{ width: 26, height: 26, borderRadius: 6, background: bg, border: `0.5px solid ${color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color }}>
                                        <HI icon={icon} size={13} />
                                      </div>
                                      <TableInput value={item.product} onChange={v => updateItem(item.id, "product", v)} width={110} />
                                    </div>
                                  ); })()}
                                </TCell>
                                <TCell><TableInput value={item.size} onChange={v => updateItem(item.id, "size", v)} width={46} /></TCell>
                                <TCell><TableInput type="number" value={item.pcs} onChange={v => updateItem(item.id, "pcs", v)} width={36} /></TCell>
                                <TCell>
                                  <select value={item.purity} onChange={e => updateItem(item.id, "purity", e.target.value)}
                                    style={{ background: "transparent", border: `0.5px solid ${purityColor}40`, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: "2px 6px", color: purityColor, fontFamily: "DM Sans, sans-serif", cursor: "pointer", outline: "none" }}>
                                    <option value="99.9">24K</option>
                                    <option value="91.6">22K</option>
                                    <option value="75">18K</option>
                                    <option value="—">—</option>
                                  </select>
                                </TCell>
                                <TCell><TableInput type="number" value={item.gwt} onChange={v => updateItem(item.id, "gwt", v)} width={50} /></TCell>
                                <TCell><TableInput type="number" value={item.swt} onChange={v => updateItem(item.id, "swt", v)} width={50} /></TCell>
                                <TCell bold>{fmtN(calc.nwt)}</TCell>
                                <TCell muted style={{ fontSize: 11 }}>₹{rates["22K"].toLocaleString("en-IN")}</TCell>
                                <TCell><TableInput type="number" value={item.making} onChange={v => updateItem(item.id, "making", v)} width={56} /></TCell>
                                <TCell><TableInput type="number" value={item.stoneAmt} onChange={v => updateItem(item.id, "stoneAmt", v)} width={56} /></TCell>
                                <TCell bold style={{ color: T.gold }}>{fmt(calc.total)}</TCell>
                                <td style={{ position: "sticky", right: 0, width: 1, padding: 0, background: T.surface }}>
                                  {hoveredRow === item.id && (
                                    <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", gap: 3, background: T.surface, borderRadius: 6, padding: 2, boxShadow: `-6px 0 12px ${T.bg}` }}>
                                      <button onClick={() => setItems(prev => {
                                        const pos = prev.findIndex(it => it.id === item.id);
                                        const copy = { ...item, id: nextId() };
                                        const next = [...prev];
                                        next.splice(pos + 1, 0, copy);
                                        return next;
                                      })} style={{ width: 22, height: 22, background: T.blueLight, border: `0.5px solid ${T.blue}40`, color: T.blue, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, transition: "all 0.15s" }}
                                        onMouseEnter={e => { e.currentTarget.style.background = T.blue; e.currentTarget.style.color = "#fff"; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = T.blueLight; e.currentTarget.style.color = T.blue; }}><HI icon={Copy01Icon} size={12} /></button>
                                      <button onClick={() => deleteRow(item.id)} style={{ width: 22, height: 22, background: T.redLight, border: `0.5px solid ${T.red}40`, color: T.red, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4, transition: "all 0.15s" }}
                                        onMouseEnter={e => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}><HI icon={Delete02Icon} size={12} /></button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>}
                    {/* Footer summary */}
                    <div style={{ display: "flex", gap: 24, padding: "9px 20px", fontSize: 11, flexWrap: "wrap" }}>
                      {[["Pieces", totalPcs], ["Gross wt", fmtN(totalGwt) + " g"], ["Stone wt", fmtN(totalSwt) + " g"], ["Net wt", fmtN(totalNwt) + " g"]].map(([l, v]) => (
                        <span key={l} style={{ color: T.textDim }}>
                          {l} <span style={{ color: T.textMuted, fontWeight: 500 }}>{v}</span>
                        </span>
                      ))}
                      <span style={{ marginLeft: "auto", color: T.textDim, fontSize: 10 }}>Prices excl. {gstType === "No GST" ? "0%" : "3%"} GST</span>
                    </div>
                  </div>

                </div>

                {/* ── Section 3: Compact Sticky Footer ── */}
                <div style={{ flexShrink: 0, borderTop: `0.5px solid ${T.surfaceBorder}`, boxShadow: "0 -4px 16px rgba(15,23,42,0.07)" }}>


                  {/* Remarks panel — expands above the main bar on demand */}
                  {showRemarks && (
                    <div style={{ animation: "fadeIn 0.15s ease" }}>
                      <div style={{ padding: "8px 20px" }}>
                        <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Internal note or customer message..."
                          rows={2} style={{ ...inputStyle, resize: "none", width: "100%", height: 52 }} />
                      </div>
                      <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 20px" }} />
                    </div>
                  )}

                  {/* Thin action strip — Scheme, Note, Print */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 20px", background: T.surfaceHigh }}>
                    <button onClick={() => setSchemeApplied(v => !v)} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "3px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                      fontFamily: "inherit", transition: "all 0.15s",
                      background: schemeApplied ? T.amber : T.amberLight,
                      border: `0.5px solid ${T.amber}`,
                      color: schemeApplied ? "#fff" : T.amber,
                    }}>
                      <HI icon={StarIcon} size={11} />
                      <span style={{ fontWeight: 600 }}>{customer.scheme.name}</span>
                      <span style={{ width: 0.5, height: 10, background: schemeApplied ? "rgba(255,255,255,0.4)" : `${T.amber}50`, display: "inline-block", flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: schemeApplied ? "#fff" : T.amber, opacity: schemeApplied ? 1 : 0.8 }}>
                        {schemeApplied ? `−${fmt(customer.scheme.credit)}` : `${customer.scheme.months}/${customer.scheme.total} mo · ${fmt(customer.scheme.credit)}`}
                      </span>
                    </button>
                    <button onClick={() => setShowRemarks(v => !v)} style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "3px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                      fontFamily: "inherit", transition: "all 0.15s",
                      background: showRemarks ? T.surfaceHigh : "transparent",
                      border: `0.5px solid ${showRemarks || remarks ? T.surfaceBorderHigh : T.surfaceBorder}`,
                      color: showRemarks || remarks ? T.text : T.textMuted,
                    }}><HI icon={PencilEdit02Icon} size={11} /> {remarks ? "Note ●" : "Note"}</button>
                  </div>
                  <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 20px" }} />

                  {/* Main compact bar */}
                  <div className="footer-main-bar" style={{ padding: "8px 20px", display: "flex", alignItems: "center", gap: 12 }}>

                    {/* Payment mode */}
                    <div className="footer-payment-zone" style={{ minWidth: 110, flex: "0 0 auto" }}>
                      <Field label="Payment mode">
                        <Select value={payMode} onChange={e => setPayMode(e.target.value)} options={["Cash", "Card", "UPI", "NEFT / RTGS", "Cheque", "Gold Scheme", "Split"]} />
                      </Field>
                    </div>

                    {/* Flow — pushed to right near CTA */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>

                      {/* Purchase */}
                      <div style={{ flexShrink: 0 }}>
                        <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1 }}>Purchase</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginTop: 3, lineHeight: 1 }}>{fmt(subtotal + gstAmt)}</div>
                      </div>

                      {advancePaid > 0 && <>
                        <span style={{ color: T.textDim, fontSize: 12, flexShrink: 0 }}>−</span>
                        <div style={{ flexShrink: 0 }}>
                          <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1 }}>Advance Paid</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.green, marginTop: 3, lineHeight: 1 }}>{fmt(advancePaid)}</div>
                        </div>
                      </>}

                      <span style={{ color: T.textDim, fontSize: 12, flexShrink: 0 }}>→</span>

                      {/* Paying Now */}
                      <div style={{ flexShrink: 0 }}>
                        <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1, marginBottom: 3 }}>Paying Now</div>
                        {payMode === "Cash"
                          ? <input type="number" value={tendered} onChange={e => setTendered(e.target.value)} placeholder="0"
                              style={{ ...inputStyle, width: 80, fontSize: 12, padding: "2px 6px" }} />
                          : <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1 }}>{fmt(grandTotal)}</div>}
                      </div>

                      <div style={{ width: 0.5, height: 32, background: T.surfaceBorder, flexShrink: 0 }} />

                      {/* Final Payable + breakup toggle */}
                      <div className="footer-total-zone" style={{ flexShrink: 0, position: "relative" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1 }}>Final Payable</div>
                          <button onClick={() => setShowBreakup(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: showBreakup ? T.gold : T.textDim, display: "flex", alignItems: "center", transition: "color 0.15s" }}>
                            <HI icon={ArrowDown01Icon} size={11} />
                          </button>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: grandTotal <= 0 ? T.green : T.text, fontFamily: "DM Serif Display, serif", lineHeight: 1.1, marginTop: 2 }}>
                          {grandTotal <= 0 ? "Settled" : fmt(grandTotal)}
                        </div>

                        {/* Breakup popover — opens upward */}
                        {showBreakup && (
                          <div style={{ position: "absolute", bottom: "calc(100% + 10px)", right: 0, width: 240, background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 10, boxShadow: "0 -4px 20px rgba(15,23,42,0.10)", zIndex: 100, overflow: "hidden" }}>
                            {/* Items */}
                            <div style={{ padding: "8px 12px 6px", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                              <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Items</div>
                              {items.map((item, idx) => {
                                const c = itemCalcs[idx];
                                return c.total > 0 ? (
                                  <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                                    <span style={{ fontSize: 11, color: T.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.product || "Item"}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: T.text, flexShrink: 0, marginLeft: 8 }}>{fmt(c.total)}</span>
                                  </div>
                                ) : null;
                              })}
                            </div>
                            {/* Calculation breakdown */}
                            <div style={{ padding: "6px 12px 8px" }}>
                              {[
                                ["Subtotal", fmt(subtotal), false],
                                ["GST", fmt(gstAmt), false],
                                schemeApplied && ["Scheme credit", `−${fmt(schemeCredit)}`, true],
                                discount > 0 && ["Discount", `−${fmt(discountAmt)}`, true],
                                advancePaid > 0 && ["Advance paid", `−${fmt(advancePaid)}`, true],
                                prevBal > 0 && ["Prev. balance", `+${fmt(prevBal)}`, false],
                              ].filter(Boolean).map(([label, value, green]) => (
                                <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                  <span style={{ fontSize: 10, color: T.textMuted }}>{label}</span>
                                  <span style={{ fontSize: 10, fontWeight: 600, color: green ? T.green : T.text }}>{value}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: `0.5px solid ${T.surfaceBorder}`, marginTop: 5, paddingTop: 5, display: "flex", justifyContent: "space-between" }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>Final Payable</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: grandTotal <= 0 ? T.green : T.text }}>{grandTotal <= 0 ? "Settled" : fmt(grandTotal)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Btn variant="primary" style={{ padding: "10px 18px", fontSize: 13, flexShrink: 0 }}><HI icon={CreditCardIcon} /> Proceed →</Btn>
                  </div>
                </div>
              </div>

            </>
          )}
          </>}
        </div>
      </div>

      {/* ── New Bill Modal ── */}
      {showNewBillModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowNewBillModal(false)}>
          <div style={{ background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 12, width: 380, boxShadow: "0 8px 32px rgba(15,23,42,0.14)", padding: "24px", animation: "fadeIn 0.15s ease" }}
            onClick={e => e.stopPropagation()}>

            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>New Bill</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Enter customer's mobile number or name to start</div>

            <input
              autoFocus
              value={newBillInput}
              onChange={e => setNewBillInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && newBillInput.trim()) confirmNewBill(newBillInput); if (e.key === "Escape") setShowNewBillModal(false); }}
              placeholder="Mobile number or customer name"
              style={{ ...inputStyle, width: "100%", padding: "10px 14px", fontSize: 13, boxSizing: "border-box", marginBottom: 16 }}
              onFocus={e => e.target.style.borderColor = T.gold}
              onBlur={e => e.target.style.borderColor = T.surfaceBorder}
            />

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn onClick={() => setShowNewBillModal(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={() => confirmNewBill(newBillInput)} style={{ opacity: newBillInput.trim() ? 1 : 0.45 }}>
                Create Bill →
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
